<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Master Facility Registry (MFR)</div>
          <div class="lookup-subtitle">Browse facilities by region, zone, and woreda.</div>
        </div>
        <v-btn
            class="lookup-add-button"
            depressed
            @click="openAddFacilityDialog"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Facility</span>
        </v-btn>
      </div>
      <div class="lookup-filters">
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                clearable
                hide-details
                label="Search location or facility code"
                outlined
                dense
                :loading="searchLoading"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
                v-model="selectedRegionId"
                :items="regionOptions"
                clearable
                dense
                hide-details
                item-text="name"
                item-value="id"
                label="Region"
                outlined
                @change="handleRegionChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
                v-model="selectedZoneId"
                :disabled="!selectedRegionId"
                :items="zoneOptions"
                clearable
                dense
                hide-details
                item-text="name"
                item-value="id"
                label="Zone"
                outlined
                @change="handleZoneChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
                v-model="selectedWoredaId"
                :disabled="!selectedZoneId"
                :items="woredaOptions"
                clearable
                dense
                hide-details
                item-text="name"
                item-value="id"
                label="Woreda"
                outlined
                @change="handleWoredaChange"
            ></v-autocomplete>
          </v-col>
        </v-row>
      </div>
      <v-row class="lookup-body" justify="space-between">
        <v-col cols="12">
          <v-treeview
              ref="treeview"
              :items="locationItems"
              :load-children="onRowClick"
              :open.sync="openNodes"
              activatable
              item-key="nodeKey"
              open-on-click
              return-object
          >
            <template v-slot:prepend="{ item }">
              <!--            <v-icon v-if="item.loading">mdi-cached</v-icon>-->
              <!--            <v-icon v-else-if="item.level < 4 && item.children.length === 0">mdi-chevron-right</v-icon>-->
              <v-icon v-if="item.level === 4">mdi-hospital-building</v-icon>
            </template>
          </v-treeview>
          <div v-if="loadingNodeId !== null" class="lookup-loading">
            <v-progress-circular
                :size="50"
                :width="4"
                color="primary"
                indeterminate
            ></v-progress-circular>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="addFacilityDialog" persistent max-width="720px">
      <v-card class="lookup-dialog-card">
        <v-card-title class="lookup-dialog__title">
          Add Facility
          <v-spacer></v-spacer>
          <v-btn icon @click="closeAddFacilityDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="lookup-dialog__body">
          <v-form ref="addFacilityForm" v-model="addFacilityFormValid">
            <v-row dense>
              <v-col cols="12" md="8">
                <v-text-field
                    v-model="addFacility.name"
                    :rules="[requiredRule]"
                    dense
                    label="Facility Name"
                    outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="addFacility.facilityType"
                    :items="facilityTypeOptions"
                    :rules="[requiredRule]"
                    dense
                    label="Facility Type"
                    outlined
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="addFacility.regionId"
                    :items="regionOptions"
                    :rules="[requiredRule]"
                    dense
                    item-text="name"
                    item-value="id"
                    label="Region"
                    outlined
                    @change="handleAddRegionChange"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="addFacility.zoneId"
                    :disabled="!addFacility.regionId"
                    :items="addZoneOptions"
                    :rules="[requiredRule]"
                    dense
                    item-text="name"
                    item-value="id"
                    label="Zone"
                    outlined
                    @change="handleAddZoneChange"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="addFacility.woredaId"
                    :disabled="!addFacility.zoneId"
                    :items="addWoredaOptions"
                    :rules="[requiredRule]"
                    dense
                    item-text="name"
                    item-value="id"
                    label="Woreda"
                    outlined
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="lookup-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddFacilityDialog">Cancel</v-btn>
          <v-btn
              class="lookup-dialog__primary"
              depressed
              :loading="savingFacility"
              @click="submitFacility"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Facility
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {MfrService} from "./mfr.service";

export default {
  data: () => ({
    search: null,
    loadingNodeId: null,
    tableloading: false,
    regionOptions: [],
    zoneOptions: [],
    woredaOptions: [],
    facilityOptions: [],
    allZoneOptions: [],
    allWoredaOptions: [],
    allFacilityOptions: [],
    addZoneOptions: [],
    addWoredaOptions: [],
    openNodes: [],
    searchLoading: false,
    searchDebounceTimer: null,
    searchCatalogsLoaded: false,
    selectedRegionId: null,
    selectedZoneId: null,
    selectedWoredaId: null,
    addFacilityDialog: false,
    addFacilityFormValid: false,
    savingFacility: false,
    facilityTypeOptions: [
      "Advanced Diagnostic",
      "Aesthetic and anti-aging",
      "Basic",
      "Basic Diagnostic Imaging",
      "Basic Health Post",
      "Cardiac Center",
      "Cardiovascular clinic",
      "Chiropractic Center",
      "Clinic",
      "Comprehensive Diagnostic Imaging",
      "Comprehensive Health Post",
      "Comprehensive Specialized Hospital",
      "Dental clinic",
      "Dental Laboratory",
      "Dental Medium Clinic",
      "Dental Speciality Clinic",
      "Dermatology clinic",
      "Dialysis Clinic",
      "Drug Store",
      "ENT Center",
      "ENT clinic",
      "Gastroenterology clinic",
      "General Hospital",
      "Health Center",
      "Health Post",
      "Home-Based Care Service",
      "Hospital",
      "Institution based clinics",
      "Institution Based Medium Clinic",
      "Institution Based Primary Clinic",
      "Internal Medicine Center",
      "Internal Medicine clinic",
      "IVF (In-vitro Fertilization) center",
      "Laboratories",
      "Maternal and Child (MCH) Center",
      "Medical Office Practice",
      "Medium Clinic",
      "Neurology Center",
      "Neurology clinic",
      "Nursing Home",
      "Obstetrics and gynecology clinic",
      "Office",
      "Oncology Speciality Clinic",
      "Ophthalmic Medium Clinic",
      "Ophthalmology",
      "Ophthalmology clinic",
      "Optometry Medium Clinic",
      "Orthopedic Center",
      "Orthopedic clinic",
      "Other",
      "Pediatric Center",
      "Pediatric clinic",
      "Pharmacy",
      "Physiotherapy Clinic",
      "Primary Clinic",
      "Primary Hospital",
      "Psychiatry and mental health clinic",
      "Psychiatry Center",
      "Regional Health Office",
      "Rehabilitation Center",
      "Specialty Clinic",
      "Surgery Center",
      "Surgery clinic",
      "Tele Health Center",
      "Trauma Center",
      "University",
      "Woreda Health Office",
      "Zonal Health Department",
    ],
    addFacility: {
      name: "",
      regionId: null,
      zoneId: null,
      woredaId: null,
      facilityType: "",
    },
    headers: [
      {text: "Id", value: "id"},
      {text: "Name", value: "name"},
    ],
    tableData: [],
    level: 1,
    title: "Federal Ministry of Health",
    locationItems: [
      {
        id: 'root',
        nodeKey: 'root',
        name: "Federal Ministry of Health",
        level: 0,
        children: []
      }
    ],
    expandedNodes: []  // Added to manage expanded nodes
  }),
  computed: {
    requiredRule() {
      return (value) => value !== null && value !== undefined && value !== "" || "This field is required.";
    },
  },
  async created() {
    await this.fetchInitialData();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  watch: {
    search(value) {
      this.queueSearch(value);
    },
  },
  methods: {
    async fetchInitialData() {
      await Promise.all([
        this.fetchData(),
        this.fetchRegionOptions(),
      ]);
    },
    async fetchRegionOptions() {
      const res = await MfrService.getRegionsDrp();
      this.regionOptions = Array.isArray(res?.data) ? res.data : [];
      await this.applySearch(this.search);
    },
    getNodeKey(id, level) {
      return `${level}-${id}`;
    },
    getNormalizedSearch(value) {
      return (value || "").toString().trim().toLowerCase();
    },
    matchesSearch(item, term) {
      if (!term) {
        return false;
      }

      return [
        item?.name,
        item?.originalName,
        item?.id,
        item?.code,
        item?.facilityType,
      ].some((value) => value !== null && value !== undefined && value.toString().toLowerCase().includes(term));
    },
    groupBy(items, key) {
      return (items || []).reduce((groups, item) => {
        const groupKey = Number(item?.[key]);
        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey).push(item);
        return groups;
      }, new Map());
    },
    queueSearch(value) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.applySearch(value);
      }, 300);
    },
    async ensureSearchCatalogs() {
      if (this.searchCatalogsLoaded) {
        return;
      }

      if (!this.regionOptions.length) {
        const regionRes = await MfrService.getRegionsDrp();
        this.regionOptions = Array.isArray(regionRes?.data) ? regionRes.data : [];
      }

      const [zoneRes, woredaRes, facilityRes] = await Promise.all([
        MfrService.getZonesDrp(),
        MfrService.getWoredasDrp(),
        MfrService.getFacilitiesDrp(),
      ]);

      this.allZoneOptions = Array.isArray(zoneRes?.data) ? zoneRes.data : [];
      this.allWoredaOptions = Array.isArray(woredaRes?.data) ? woredaRes.data : [];
      this.allFacilityOptions = Array.isArray(facilityRes?.data) ? facilityRes.data : [];
      this.searchCatalogsLoaded = true;
    },
    async applySearch(value) {
      const term = this.getNormalizedSearch(value);
      if (!term) {
        this.openNodes = [];
        this.buildFilteredTree();
        return;
      }

      this.searchLoading = true;
      try {
        await this.ensureSearchCatalogs();
        this.buildSearchTree(term);
      } catch (error) {
        this.$toast.error("There was a problem searching MFR.");
      } finally {
        this.searchLoading = false;
      }
    },
    buildSearchTree(term) {
      const zonesByRegion = this.groupBy(this.allZoneOptions, "regionId");
      const woredasByZone = this.groupBy(this.allWoredaOptions, "zoneId");
      const facilitiesByWoreda = this.groupBy(this.allFacilityOptions, "woredaId");
      const scopedRegionId = this.selectedRegionId ? Number(this.selectedRegionId) : null;
      const scopedZoneId = this.selectedZoneId ? Number(this.selectedZoneId) : null;
      const scopedWoredaId = this.selectedWoredaId ? Number(this.selectedWoredaId) : null;
      const openNodes = [];

      const regionNodes = this.regionOptions
          .filter((region) => !scopedRegionId || Number(region?.id) === scopedRegionId)
          .map((region) => {
            const regionMatched = this.matchesSearch(region, term);
            const zoneNodes = (zonesByRegion.get(Number(region?.id)) || [])
                .filter((zone) => !scopedZoneId || Number(zone?.id) === scopedZoneId)
                .map((zone) => {
                  const zoneMatched = this.matchesSearch(zone, term);
                  const woredaNodes = (woredasByZone.get(Number(zone?.id)) || [])
                      .filter((woreda) => !scopedWoredaId || Number(woreda?.id) === scopedWoredaId)
                      .map((woreda) => {
                        const woredaMatched = this.matchesSearch(woreda, term);
                        const includeAllFacilities = regionMatched || zoneMatched || woredaMatched;
                        const facilityNodes = (facilitiesByWoreda.get(Number(woreda?.id)) || [])
                            .filter((facility) => includeAllFacilities || this.matchesSearch(facility, term))
                            .map((facility) => this.buildLocationNode(facility, 4));

                        if (!regionMatched && !zoneMatched && !woredaMatched && facilityNodes.length === 0) {
                          return null;
                        }

                        const woredaNode = this.buildLocationNode(woreda, 3);
                        woredaNode.children = facilityNodes;
                        openNodes.push(woredaNode.nodeKey);
                        return woredaNode;
                      })
                      .filter(Boolean);

                  if (!regionMatched && !zoneMatched && woredaNodes.length === 0) {
                    return null;
                  }

                  const zoneNode = this.buildLocationNode(zone, 2);
                  zoneNode.children = woredaNodes;
                  openNodes.push(zoneNode.nodeKey);
                  return zoneNode;
                })
                .filter(Boolean);

            if (!regionMatched && zoneNodes.length === 0) {
              return null;
            }

            const regionNode = this.buildLocationNode(region, 1);
            regionNode.children = zoneNodes;
            openNodes.push(regionNode.nodeKey);
            return regionNode;
          })
          .filter(Boolean);

      this.locationItems = [
        {
          id: 'root',
          nodeKey: 'root',
          name: "Federal Ministry of Health",
          level: 0,
          children: regionNodes,
        },
      ];
      this.openNodes = ['root', ...openNodes];
    },
    async fetchData(id = null, level = this.level, parentNodeKey = null) {
      this.tableloading = true;
      let res = null;
      try {
        if (level === 1) {
          res = await MfrService.getRegionsDrp();
        } else if (level === 2) {
          res = await MfrService.getZonesDrp(id);
        } else if (level === 3) {
          res = await MfrService.getWoredasDrp(id);
        } else if (level === 4) {
          res = await MfrService.getFacilitiesDrp(id);
        }

        if (res.status === 200) {
          this.tableData = res.data;
          const items = res.data.map((item) => this.buildLocationNode(item, level));

          if (id) {
            const parent = this.findNode(this.locationItems, parentNodeKey || this.getNodeKey(id, level - 1));
            if (parent) {
              parent.children = items;
            }
          } else if (!this.selectedRegionId && !this.selectedZoneId && !this.selectedWoredaId) {
            this.locationItems[0].children = items;
          }
        } else {
          this.$toast.error("There was a problem fetching data!");
        }
      } catch (error) {
        this.$toast.error("There was a problem fetching data!");
      } finally {
        this.tableloading = false;
      }
    },
    buildLocationNode(item, level) {
      return {
        ...item,
        nodeKey: this.getNodeKey(item?.id, level),
        name: level === 4 ? this.formatFacilityLabel(item) : item?.name,
        originalName: item?.name,
        level,
        children: level < 4 ? [] : null,
        type: level === 4 ? 'facility' : undefined,
        loading: false,
      };
    },
    formatFacilityLabel(item) {
      const name = (item?.name || "").toString().trim();
      const code = item?.id ?? null;
      if (name && code !== null && code !== undefined) {
        return `${name} (${code})`;
      }
      return name || (code !== null && code !== undefined ? String(code) : "-");
    },
    buildFilteredTree() {
      const openNodes = [];
      const regionSource = this.selectedRegionId
          ? this.regionOptions.filter((item) => Number(item?.id) === Number(this.selectedRegionId))
          : this.regionOptions;
      const regionNodes = regionSource.map((item) => this.buildLocationNode(item, 1));

      if (this.selectedRegionId) {
        const regionNode = regionNodes.find((item) => Number(item?.id) === Number(this.selectedRegionId));
        if (regionNode) {
          const zoneSource = this.selectedZoneId
              ? this.zoneOptions.filter((item) => Number(item?.id) === Number(this.selectedZoneId))
              : this.zoneOptions;
          regionNode.children = zoneSource.map((item) => this.buildLocationNode(item, 2));

          if (this.selectedZoneId) {
            const zoneNode = regionNode.children.find((item) => Number(item?.id) === Number(this.selectedZoneId));
            if (zoneNode) {
              const woredaSource = this.selectedWoredaId
                  ? this.woredaOptions.filter((item) => Number(item?.id) === Number(this.selectedWoredaId))
                  : this.woredaOptions;
              zoneNode.children = woredaSource.map((item) => this.buildLocationNode(item, 3));

              if (this.selectedWoredaId) {
                const woredaNode = zoneNode.children.find((item) => Number(item?.id) === Number(this.selectedWoredaId));
                if (woredaNode) {
                  woredaNode.children = this.facilityOptions.map((item) => this.buildLocationNode(item, 4));
                }
              }
            }
          }
        }
      }

      this.locationItems = [
        {
          id: 'root',
          nodeKey: 'root',
          name: "Federal Ministry of Health",
          level: 0,
          children: regionNodes,
        },
      ];

      if (this.selectedRegionId) {
        openNodes.push('root', this.getNodeKey(this.selectedRegionId, 1));
      }
      if (this.selectedZoneId) {
        openNodes.push(this.getNodeKey(this.selectedZoneId, 2));
      }
      if (this.selectedWoredaId) {
        openNodes.push(this.getNodeKey(this.selectedWoredaId, 3));
      }
      this.openNodes = openNodes;
    },
    async handleRegionChange(value) {
      this.selectedRegionId = value || null;
      this.selectedZoneId = null;
      this.selectedWoredaId = null;
      this.zoneOptions = [];
      this.woredaOptions = [];
      this.facilityOptions = [];

      if (this.selectedRegionId) {
        const res = await MfrService.getZonesDrp(this.selectedRegionId);
        this.zoneOptions = Array.isArray(res?.data) ? res.data : [];
      }

      await this.applySearch(this.search);
    },
    async handleZoneChange(value) {
      this.selectedZoneId = value || null;
      this.selectedWoredaId = null;
      this.woredaOptions = [];
      this.facilityOptions = [];

      if (this.selectedZoneId) {
        const res = await MfrService.getWoredasDrp(this.selectedZoneId);
        this.woredaOptions = Array.isArray(res?.data) ? res.data : [];
      }

      await this.applySearch(this.search);
    },
    async handleWoredaChange(value) {
      this.selectedWoredaId = value || null;
      this.facilityOptions = [];

      if (this.selectedWoredaId) {
        const res = await MfrService.getFacilitiesDrp(this.selectedWoredaId);
        this.facilityOptions = Array.isArray(res?.data) ? res.data : [];
      }

      await this.applySearch(this.search);
    },
    getEmptyFacilityForm() {
      return {
        name: "",
        regionId: this.selectedRegionId || null,
        zoneId: this.selectedZoneId || null,
        woredaId: this.selectedWoredaId || null,
        facilityType: "",
      };
    },
    async openAddFacilityDialog() {
      this.addFacility = this.getEmptyFacilityForm();
      this.addZoneOptions = this.selectedRegionId ? [...this.zoneOptions] : [];
      this.addWoredaOptions = this.selectedZoneId ? [...this.woredaOptions] : [];
      this.addFacilityDialog = true;

      if (this.addFacility.regionId && !this.addZoneOptions.length) {
        const zoneRes = await MfrService.getZonesDrp(this.addFacility.regionId);
        this.addZoneOptions = Array.isArray(zoneRes?.data) ? zoneRes.data : [];
      }
      if (this.addFacility.zoneId && !this.addWoredaOptions.length) {
        const woredaRes = await MfrService.getWoredasDrp(this.addFacility.zoneId);
        this.addWoredaOptions = Array.isArray(woredaRes?.data) ? woredaRes.data : [];
      }

      this.$nextTick(() => {
        this.$refs.addFacilityForm?.resetValidation?.();
      });
    },
    closeAddFacilityDialog() {
      this.addFacilityDialog = false;
      this.savingFacility = false;
      this.addZoneOptions = [];
      this.addWoredaOptions = [];
      this.addFacility = this.getEmptyFacilityForm();
    },
    async handleAddRegionChange(value) {
      this.addFacility.regionId = value || null;
      this.addFacility.zoneId = null;
      this.addFacility.woredaId = null;
      this.addZoneOptions = [];
      this.addWoredaOptions = [];

      if (this.addFacility.regionId) {
        const res = await MfrService.getZonesDrp(this.addFacility.regionId);
        this.addZoneOptions = Array.isArray(res?.data) ? res.data : [];
      }
    },
    async handleAddZoneChange(value) {
      this.addFacility.zoneId = value || null;
      this.addFacility.woredaId = null;
      this.addWoredaOptions = [];

      if (this.addFacility.zoneId) {
        const res = await MfrService.getWoredasDrp(this.addFacility.zoneId);
        this.addWoredaOptions = Array.isArray(res?.data) ? res.data : [];
      }
    },
    async submitFacility() {
      if (!this.$refs.addFacilityForm?.validate?.()) return;

      this.savingFacility = true;
      try {
        const payload = {
          name: this.addFacility.name,
          woredaId: Number(this.addFacility.woredaId),
          facilityType: this.addFacility.facilityType || null,
        };
        const res = await MfrService.createFacility(payload);
        if ((res?.status === 200 || res?.status === 201) && res?.data?.status === 200) {
          this.$toast.success(res.data.message || "Facility registered successfully!");
          this.searchCatalogsLoaded = false;
          await this.refreshAfterFacilityCreate();
          this.closeAddFacilityDialog();
        } else {
          this.$toast.error(res?.data?.message || "Unable to create facility.");
        }
      } catch (error) {
        this.$toast.error("Unable to create facility.");
      } finally {
        this.savingFacility = false;
      }
    },
    async refreshAfterFacilityCreate() {
      this.search = null;
      this.selectedRegionId = this.addFacility.regionId;
      this.selectedZoneId = this.addFacility.zoneId;
      this.selectedWoredaId = this.addFacility.woredaId;

      const [zoneRes, woredaRes, facilityRes] = await Promise.all([
        MfrService.getZonesDrp(this.selectedRegionId),
        MfrService.getWoredasDrp(this.selectedZoneId),
        MfrService.getFacilitiesDrp(this.selectedWoredaId),
      ]);

      this.zoneOptions = Array.isArray(zoneRes?.data) ? zoneRes.data : [];
      this.woredaOptions = Array.isArray(woredaRes?.data) ? woredaRes.data : [];
      this.facilityOptions = Array.isArray(facilityRes?.data) ? facilityRes.data : [];
      this.buildFilteredTree();
    },
    async onRowClick(item) {
      item = item?.nodeKey ? item : item[0];
      if (!item) return;
      if (item.type === 'facility') return;

      if (Array.isArray(item.children) && item.children.length === 0 && !item.loading) {
        item.loading = true;
        this.loadingNodeId = item.nodeKey;
        await this.fetchData(item.id, item.level + 1, item.nodeKey);
        item.loading = false;
        this.loadingNodeId = null;
      }

      this.level = item.level + 1;
      this.title = item.name;
    },
    findNode(items, nodeKey) {
      for (const item of items) {
        if (item.nodeKey === nodeKey) return item;
        if (item.children && item.children.length) {
          const found = this.findNode(item.children, nodeKey);
          if (found) return found;
        }
      }
      return null;
    }
  }
};
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  padding: 24px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-add-button,
.lookup-dialog__primary {
  background: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-dialog__body {
  padding: 20px 24px 4px;
}

.lookup-dialog__actions {
  padding: 8px 24px 20px;
}

.lookup-filters {
  padding: 0 28px 20px;
}

.lookup-body {
  padding: 0 28px 24px;
}

.lookup-loading {
  text-align: center;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    padding: 20px;
    align-items: stretch;
    flex-direction: column;
  }

  .lookup-filters {
    padding: 0 20px 20px;
  }

  .lookup-body {
    padding: 0 20px 20px;
  }
}
</style>
